/**
 * Centralized backend API path catalog.
 *
 * Single source of truth for every shore HTTP endpoint the webui calls.
 * All `*.api.ts` modules MUST build URLs via `apiPaths.<feature>.<op>(...)`,
 * never with `` `/workspaces/${id}/...` `` template literals.
 *
 * Why one file: changing the API base (e.g. `/workspaces` to
 * `/api/v2/workspaces`) is a 1-line edit to `WORKSPACE_API_BASE`.
 * Renaming a shore endpoint is a 1-line edit to the matching builder.
 *
 * Query strings stay at the call site, callers compose a URLSearchParams
 * suffix per request and append it to the path returned here.
 *
 * The audit (`scripts/audit.sh` rule RT2) treats this file as the only
 * allowlist for literal `/workspaces/${...}` templates. The Vite dev
 * proxy in `vite.config.ts` mirrors `WORKSPACE_API_BASE`.
 */

const WORKSPACE_API_BASE = "/workspaces" as const;

const ws = (workspaceId: string) => `${WORKSPACE_API_BASE}/${workspaceId}`;

export const apiPaths = {
  /// /workspaces lifecycle
  workspaces: () => `${WORKSPACE_API_BASE}`,
  workspace: ws,
  workspaceRestore: (id: string) => `${ws(id)}/restore`,
  workspaceCompanyProfile: (id: string) => `${ws(id)}/company-profile`,
  workspaceCompanyProfileLogo: (id: string) => `${ws(id)}/company-profile/logo`,

  /// Auth (no workspace prefix)
  auth: {
    me: () => `/auth/me`,
    signupRequest: () => `/auth/signup/request`,
    signupVerify: () => `/auth/signup/verify`,
    signupComplete: () => `/auth/signup/complete`,
    loginRequest: () => `/auth/login/request`,
    loginVerify: () => `/auth/login/verify`,
    loginPassword: () => `/auth/login/password`,
    loginPickAccount: () => `/auth/login/pick-account`,
    passwordForgot: () => `/auth/password/forgot`,
    passwordReset: () => `/auth/password/reset`,
    passwordChange: () => `/auth/password/change`,
    signOut: () => `/auth/signout`,
    googleStart: () => `/auth/google/start`,
    googlePickAccount: () => `/auth/google/pick-account`,
  },

  /// The consent API behind the hosted MCP Connect screen. Session cookie
  /// auth; the OAuth protocol endpoints themselves are called by AI apps,
  /// never by the webui.
  oauth: {
    request: (requestId: string) => `/oauth/requests/${requestId}`,
    approve: (requestId: string) => `/oauth/requests/${requestId}/approve`,
    deny: (requestId: string) => `/oauth/requests/${requestId}/deny`,
  },

  /// Copilot conversations are user-owned and workspace is a query/body scope.
  copilot: {
    currentConversation: (workspaceId: string) =>
      `/copilot/conversations/current?workspaceId=${encodeURIComponent(workspaceId)}`,
    conversations: () => `/copilot/conversations`,
    messages: (conversationId: string) => `/copilot/conversations/${conversationId}/messages`,
    stop: (conversationId: string) => `/copilot/conversations/${conversationId}/stop`,
  },

  /// /billing (no workspace prefix; plans/checkout/redeem are account or
  /// anonymous-buyer scoped, not workspace-scoped)
  billing: {
    plans: () => `/billing/plans`,
    checkout: () => `/billing/checkout`,
    orderStatus: (id: string) => `/billing/orders/${id}/status`,
    redeem: () => `/billing/redeem`,
    me: () => `/billing/me`,
  },

  /// Public job applications (no workspace prefix, no session). The shore
  /// resolves the opening token to a workspace server side, which is exactly
  /// why no id appears here: a URL that named the workspace would hand a
  /// stranger a tenant identifier to probe with.
  ///
  /// `opening` is the read half: the job post itself (title, company,
  /// department, location, closing date) so the page can name the role before
  /// it asks for a CV. It returns the published posting and nothing else, so
  /// a leaked link is worth what the job ad is worth; see
  /// `recruitment/public/public-opening.schema.ts` for the bound on that body.
  hrPublic: {
    opening: (openingToken: string) => `/public/openings/${openingToken}`,
    apply: (openingToken: string) => `/public/job-applications/${openingToken}`,
  },

  /// Public invitation flow (no workspace prefix)
  invitations: {
    accept: () => `/invitations/accept`,
    acceptComplete: () => `/invitations/accept/complete`,
    lookupError: () => `/invitations/lookup-error`,
  },

  /// /accounts/:accountId
  account: {
    detail: (accountId: string) => `/accounts/${accountId}`,
    admins: (accountId: string) => `/accounts/${accountId}/admins`,
    admin: (accountId: string, userId: string) => `/accounts/${accountId}/admins/${userId}`,
    workspaces: (accountId: string) => `/accounts/${accountId}/workspaces`,
    aiConnections: {
      list: (accountId: string, cursor?: string) =>
        `/accounts/${accountId}/ai-connections${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
      revoke: (accountId: string, grantId: string) =>
        `/accounts/${accountId}/ai-connections/${grantId}/revoke`,
      settings: (accountId: string) => `/accounts/${accountId}/ai-connections/settings`,
    },
  },

  /// /me
  me: {
    detail: () => `/me`,
    avatar: () => `/me/avatar`,
    summary: (id: string) => `${ws(id)}/me/summary`,
    entitlements: () => `/me/entitlements`,
    tokens: {
      list: () => `/me/tokens`,
      create: () => `/me/tokens`,
      revoke: (tokenId: string) => `/me/tokens/${tokenId}/revoke`,
    },
    aiConnections: {
      list: () => `/me/ai-connections`,
      disconnect: (grantId: string) => `/me/ai-connections/${grantId}/disconnect`,
    },
  },

  /// /workspaces/:wsId/members
  members: {
    list: (id: string) => `${ws(id)}/members`,
    member: (id: string, mId: string) => `${ws(id)}/members/${mId}`,
    profile: (id: string, mId: string) => `${ws(id)}/members/${mId}/profile`,
    leave: (id: string) => `${ws(id)}/members/leave`,
  },

  /// /workspaces/:wsId/invitations
  invites: {
    list: (id: string) => `${ws(id)}/invitations`,
    detail: (id: string, inviteId: string) => `${ws(id)}/invitations/${inviteId}`,
    resend: (id: string, inviteId: string) => `${ws(id)}/invitations/${inviteId}/resend`,
    resendMember: (id: string) => `${ws(id)}/invitations/resend-member`,
    bulkResend: (id: string) => `${ws(id)}/invitations/bulk-resend`,
    bulkRevoke: (id: string) => `${ws(id)}/invitations/bulk-revoke`,
  },

  /// /workspaces/:wsId/feedbacks (+ /admin/feedbacks)
  feedback: {
    list: (id: string) => `${ws(id)}/feedbacks`,
    stats: (id: string) => `${ws(id)}/feedbacks/stats`,
    detail: (id: string, fId: string) => `${ws(id)}/feedbacks/${fId}`,
    acknowledge: (id: string, fId: string) => `${ws(id)}/feedbacks/${fId}/acknowledge`,
    retryAnalysis: (id: string, fId: string) => `${ws(id)}/feedbacks/${fId}/retry-analysis`,
    spawnedTodos: (id: string, fId: string) => `${ws(id)}/feedbacks/${fId}/spawned-todos`,
    actionPlan: (id: string, fId: string) => `${ws(id)}/feedbacks/${fId}/action-plan`,
    actionPlanItem: (id: string, fId: string, idx: number) =>
      `${ws(id)}/feedbacks/${fId}/action-plan/${idx}`,
    promoteToTodo: (id: string, fId: string, idx: number) =>
      `${ws(id)}/feedbacks/${fId}/action-plan/${idx}/promote-to-todo`,
    admin: (id: string) => `${ws(id)}/admin/feedbacks`,
  },

  /// /workspaces/:wsId/todos (+ /admin/todos + bulk)
  todos: {
    list: (id: string) => `${ws(id)}/todos`,
    counts: (id: string) => `${ws(id)}/todos/counts`,
    grouped: (id: string) => `${ws(id)}/todos/grouped`,
    admin: (id: string) => `${ws(id)}/admin/todos`,
    detail: (id: string, todoId: string) => `${ws(id)}/todos/${todoId}`,
    assignees: (id: string, todoId: string) => `${ws(id)}/todos/${todoId}/assignees`,
    status: (id: string, todoId: string) => `${ws(id)}/todos/${todoId}/status`,
    complete: (id: string, todoId: string) => `${ws(id)}/todos/${todoId}/complete`,
    order: (id: string, todoId: string) => `${ws(id)}/todos/${todoId}/order`,
    links: (id: string, todoId: string) => `${ws(id)}/todos/${todoId}/links`,
    link: (id: string, todoId: string, linkId: string) =>
      `${ws(id)}/todos/${todoId}/links/${linkId}`,
    bulkComplete: (id: string) => `${ws(id)}/todos/bulk/complete`,
    bulkReassign: (id: string) => `${ws(id)}/todos/bulk/reassign`,
    bulkDelete: (id: string) => `${ws(id)}/todos/bulk/delete`,
  },

  /// /workspaces/:wsId/goals (+ /admin, tree, alignment, check-ins)
  goals: {
    list: (id: string) => `${ws(id)}/goals`,
    admin: (id: string) => `${ws(id)}/admin/goals`,
    detail: (id: string, goalId: string) => `${ws(id)}/goals/${goalId}`,
    checkInContext: (id: string, goalId: string) => `${ws(id)}/goals/${goalId}/check-in-context`,
    tree: (id: string, goalId: string) => `${ws(id)}/goals/${goalId}/tree`,
    assignees: (id: string, goalId: string) => `${ws(id)}/goals/${goalId}/assignees`,
    progress: (id: string, goalId: string) => `${ws(id)}/goals/${goalId}/progress`,
    alignment: (id: string, goalId: string) => `${ws(id)}/goals/${goalId}/alignment`,
    checkIns: (id: string, goalId: string) => `${ws(id)}/goals/${goalId}/check-ins`,
    checkIn: (id: string, goalId: string, checkInId: string) =>
      `${ws(id)}/goals/${goalId}/check-ins/${checkInId}`,
    tasks: (id: string, goalId: string) => `${ws(id)}/goals/${goalId}/tasks`,
    task: (id: string, goalId: string, todoId: string) =>
      `${ws(id)}/goals/${goalId}/tasks/${todoId}`,
    risks: (id: string, goalId: string) => `${ws(id)}/goals/${goalId}/risks`,
    risk: (id: string, goalId: string, riskId: string) =>
      `${ws(id)}/goals/${goalId}/risks/${riskId}`,
    riskClose: (id: string, goalId: string, riskId: string) =>
      `${ws(id)}/goals/${goalId}/risks/${riskId}/close`,
  },

  /// /workspaces/:wsId/kudos
  /// /workspaces/:wsId/attendance (M16)
  attendance: {
    setupStatus: (id: string) => `${ws(id)}/attendance/setup-status`,
    records: (id: string) => `${ws(id)}/attendance/records`,
    register: (id: string) => `${ws(id)}/attendance-register`,
    registerExportCsv: (id: string) => `${ws(id)}/attendance-register/export.csv`,
    myRecords: (id: string) => `${ws(id)}/attendance/records/me`,
    record: (id: string, attendanceId: string) => `${ws(id)}/attendance/records/${attendanceId}`,
    cancelRecord: (id: string, attendanceId: string) =>
      `${ws(id)}/attendance/records/${attendanceId}/cancel`,
    day: (id: string) => `${ws(id)}/attendance/day`,
    myDay: (id: string) => `${ws(id)}/attendance/my-day`,
    holidays: (id: string) => `${ws(id)}/attendance/holidays`,
    presence: (id: string) => `${ws(id)}/attendance/presence`,
    checkins: (id: string) => `${ws(id)}/attendance/checkins`,
    lastCheckin: (id: string) => `${ws(id)}/attendance/checkins/last`,
    requests: (id: string) => `${ws(id)}/attendance/requests`,
    cancelRequest: (id: string, requestId: string) =>
      `${ws(id)}/attendance/requests/${requestId}/cancel`,
    withdrawRequest: (id: string, requestId: string) =>
      `${ws(id)}/attendance/requests/${requestId}/withdraw`,
    calendars: (id: string) => `${ws(id)}/attendance/calendars`,
    calendar: (id: string, calendarId: string) => `${ws(id)}/attendance/calendars/${calendarId}`,
    calendarWeekOffs: (id: string, calendarId: string) =>
      `${ws(id)}/attendance/calendars/${calendarId}/week-offs`,
    calendarDefault: (id: string, calendarId: string) =>
      `${ws(id)}/attendance/calendars/${calendarId}/default`,
    calendarHolidays: (id: string, calendarId: string) =>
      `${ws(id)}/attendance/calendars/${calendarId}/holidays`,
    calendarWorkingDay: (id: string, calendarId: string) =>
      `${ws(id)}/attendance/calendars/${calendarId}/working-days`,
    defaultWorkingDay: (id: string) => `${ws(id)}/attendance/calendars/default/working-days`,
    calendarHoliday: (id: string, calendarId: string, holidayId: string) =>
      `${ws(id)}/attendance/calendars/${calendarId}/holidays/${holidayId}`,
    holidayCatalogCountries: (id: string) => `${ws(id)}/attendance/holiday-catalog/countries`,
    holidayCatalogSubdivisions: (id: string, country: string) =>
      `${ws(id)}/attendance/holiday-catalog/countries/${country}/subdivisions`,
    holidayImportPreview: (id: string, calendarId: string) =>
      `${ws(id)}/attendance/calendars/${calendarId}/holiday-import/preview`,
    holidayImport: (id: string, calendarId: string) =>
      `${ws(id)}/attendance/calendars/${calendarId}/holiday-import`,
    shifts: (id: string) => `${ws(id)}/attendance/shifts`,
    shift: (id: string, shiftId: string) => `${ws(id)}/attendance/shifts/${shiftId}`,
    shiftProcess: (id: string, shiftId: string) => `${ws(id)}/attendance/shifts/${shiftId}/process`,
    assignments: (id: string) => `${ws(id)}/attendance/shift-assignments`,
    assignment: (id: string, assignmentId: string) =>
      `${ws(id)}/attendance/shift-assignments/${assignmentId}`,
    assignmentsBulk: (id: string) => `${ws(id)}/attendance/shift-assignments/bulk`,
    settings: (id: string) => `${ws(id)}/attendance/settings`,
    approveRequest: (id: string, requestId: string) =>
      `${ws(id)}/attendance/requests/${requestId}/approve`,
    rejectRequest: (id: string, requestId: string) =>
      `${ws(id)}/attendance/requests/${requestId}/reject`,
  },

  kudos: {
    list: (id: string) => `${ws(id)}/kudos`,
    detail: (id: string, kudosId: string) => `${ws(id)}/kudos/${kudosId}`,
  },

  /// /workspaces/:wsId compensation (module 80, HRMS-CMP-001 owns the shore
  /// spellings; frontend-ia section 10 the builders). Later comp stories
  /// append their builders here; nothing outside this namespace may spell a
  /// comp path (RT2).
  compensation: {
    profile: (id: string) => `${ws(id)}/comp-profile`,
    cycles: (id: string) => `${ws(id)}/comp-cycles`,
    cycle: (id: string, cycleId: string) => `${ws(id)}/comp-cycles/${cycleId}`,
    /// Sub-resources of one cycle ("summary" here; later stories add
    /// "config", "budget", ...). One builder so the spelling lives once.
    cycleSub: (id: string, cycleId: string, sub: string) =>
      `${ws(id)}/comp-cycles/${cycleId}/${sub}`,
    /// HRMS-CMP-010 bands (CMP-013 consumes; CMP-061 reuses bandResolve).
    bands: (id: string) => `${ws(id)}/salary-bands`,
    band: (id: string, bandId: string) => `${ws(id)}/salary-bands/${bandId}`,
    bandResolve: (id: string) => `${ws(id)}/salary-bands/resolve`,
    bandSuggest: (id: string) => `${ws(id)}/salary-bands/suggest`,
    bandMine: (id: string) => `${ws(id)}/salary-bands/mine`,
    /// HRMS-CMP-011 bonus plan item routes (collection lives under cycleSub).
    bonusPlan: (id: string, cycleId: string, planId: string) =>
      `${ws(id)}/comp-cycles/${cycleId}/bonus-plans/${planId}`,
    cycleAction: (id: string, cycleId: string, action: string) =>
      `${ws(id)}/comp-cycles/${cycleId}/${action}`,
    reviewCycles: (id: string, cycleId: string) => `${ws(id)}/comp-cycles/${cycleId}/review-cycles`,
    reviewCyclesEligible: (id: string, cycleId: string) =>
      `${ws(id)}/comp-cycles/${cycleId}/review-cycles/eligible`,
    reviewCyclesPreview: (id: string, cycleId: string) =>
      `${ws(id)}/comp-cycles/${cycleId}/review-cycles/preview`,
    entry: (id: string, entryId: string) => `${ws(id)}/comp-entries/${entryId}`,
    entryAction: (id: string, entryId: string, action: string) =>
      `${ws(id)}/comp-entries/${entryId}/${action}`,
    worksheet: (id: string, worksheetId: string) => `${ws(id)}/comp-worksheets/${worksheetId}`,
    assignedEntry: (id: string, worksheetId: string, entryId: string) =>
      `${ws(id)}/comp-worksheets/${worksheetId}/entries/${entryId}`,
    worksheetsMine: (id: string) => `${ws(id)}/comp-worksheets/mine`,
    worksheetAction: (id: string, worksheetId: string, action: string) =>
      `${ws(id)}/comp-worksheets/${worksheetId}/${action}`,
    annualBudgets: (id: string) => `${ws(id)}/comp-annual-budgets`,
    annualBudget: (id: string, annualBudgetId: string) =>
      `${ws(id)}/comp-annual-budgets/${annualBudgetId}`,
    allocation: (id: string, allocationId: string) => `${ws(id)}/comp-allocations/${allocationId}`,
    allocationMembers: (id: string, allocationId: string) =>
      `${ws(id)}/comp-allocations/${allocationId}/members`,
    lettersMine: (id: string) => `${ws(id)}/comp-letters/mine`,
    letter: (id: string, letterId: string) => `${ws(id)}/comp-letters/${letterId}`,
    letterAction: (id: string, letterId: string, action: string) =>
      `${ws(id)}/comp-letters/${letterId}/${action}`,
    offers: (id: string) => `${ws(id)}/comp-offers`,
    offerAction: (id: string, offerId: string, action: string) =>
      `${ws(id)}/comp-offers/${offerId}/${action}`,
  },

  /// /workspaces/:wsId/leave (M17, HRMS leave)
  /// Module 40 payroll (PAY-005). Spellings follow the shore's api-surface,
  /// which owns them; the frontend-ia sketch is a sketch (CC3).
  /**
   * My pay details (ESS-01). Its own group rather than entries under
   * `payroll`, because three features call these (D9) and a group named for
   * the page is easier to find than eleven builders scattered through the
   * payroll block.
   */
  payDetails: {
    sensitive: (id: string, membershipId: string) =>
      `${ws(id)}/employee-profiles/${membershipId}/sensitive`,
    /// `reveal=1` needs the workspace key. A refused reveal is answered with
    /// the masked shape, not an error, so the caller cannot tell a refusal
    /// from a field that was never set.
    sensitiveReveal: (id: string, membershipId: string) =>
      `${ws(id)}/employee-profiles/${membershipId}/sensitive?reveal=1`,
    confirmBank: (id: string) => `${ws(id)}/employee-profiles/sensitive/confirm-bank`,
    readiness: (id: string, membershipId: string) =>
      `${ws(id)}/employee-profiles/${membershipId}/payroll-readiness`,
    changeRequests: (id: string) => `${ws(id)}/profile-change-requests`,
    activateLegalName: (id: string, requestId: string) =>
      `${ws(id)}/profile-change-requests/${requestId}/activate-legal-name`,
    changeRequest: (id: string, requestId: string) =>
      `${ws(id)}/profile-change-requests/${requestId}`,
    withdrawChangeRequest: (id: string, requestId: string) =>
      `${ws(id)}/profile-change-requests/${requestId}/withdraw`,
  },

  /// Module 50: recruitment (REC-000). Every path design.md sections 5.1 to
  /// 5.13 defines, landed with the substrate so each river story finds its
  /// builder already spelled instead of eight lanes appending to this file.
  /// Nothing serves these yet.
  recruitment: {
    /// 5.1 staffing plans.
    staffingPlans: (id: string) => `${ws(id)}/staffing-plans`,
    staffingPlan: (id: string, planId: string) => `${ws(id)}/staffing-plans/${planId}`,

    /// 5.2 job requisitions. `scope` (own | workspace) is a query param.
    jobRequisitions: (id: string) => `${ws(id)}/job-requisitions`,
    jobRequisition: (id: string, requisitionId: string) =>
      `${ws(id)}/job-requisitions/${requisitionId}`,
    jobRequisitionCancel: (id: string, requisitionId: string) =>
      `${ws(id)}/job-requisitions/${requisitionId}/cancel`,

    /// 5.3 job openings. Publish and unpublish are POSTs because each is a
    /// state transition with a version body, not a field edit.
    jobOpenings: (id: string) => `${ws(id)}/job-openings`,
    jobOpening: (id: string, openingId: string) => `${ws(id)}/job-openings/${openingId}`,
    jobOpeningPublish: (id: string, openingId: string) =>
      `${ws(id)}/job-openings/${openingId}/publish`,
    jobOpeningUnpublish: (id: string, openingId: string) =>
      `${ws(id)}/job-openings/${openingId}/unpublish`,
    jobOpeningClose: (id: string, openingId: string) => `${ws(id)}/job-openings/${openingId}/close`,

    /// 5.5 applicants. `view=kanban` switches GET to the 5.10 board shape.
    jobApplicants: (id: string) => `${ws(id)}/job-applicants`,
    jobApplicant: (id: string, applicantId: string) => `${ws(id)}/job-applicants/${applicantId}`,
    jobApplicantMoveStage: (id: string, applicantId: string) =>
      `${ws(id)}/job-applicants/${applicantId}/move-stage`,
    /// Its own action sub-resource, not a second move-stage: a move starts the
    /// stage clock, an undo restores the one the move replaced. Body is the
    /// version the move-stage RESPONSE carried, and nothing else.
    jobApplicantUndoStageMove: (id: string, applicantId: string) =>
      `${ws(id)}/job-applicants/${applicantId}/undo-stage-move`,
    jobApplicantConvertToEmployee: (id: string, applicantId: string) =>
      `${ws(id)}/job-applicants/${applicantId}/convert-to-employee`,

    /// 5.6 interview rounds (a workspace catalog, not per applicant).
    interviewRounds: (id: string) => `${ws(id)}/interview-rounds`,
    interviewRound: (id: string, roundId: string) => `${ws(id)}/interview-rounds/${roundId}`,

    /// 5.7 interviews. `scope` (own | workspace) is a query param.
    interviews: (id: string) => `${ws(id)}/interviews`,
    interview: (id: string, interviewId: string) => `${ws(id)}/interviews/${interviewId}`,
    /// One path, two verbs: POST submits the caller's own feedback,
    /// GET reads every panel member's plus the per-skill averages.
    interviewFeedback: (id: string, interviewId: string) =>
      `${ws(id)}/interviews/${interviewId}/feedback`,

    /// 5.8 offer term templates.
    offerTermTemplates: (id: string) => `${ws(id)}/offer-term-templates`,
    offerTermTemplate: (id: string, templateId: string) =>
      `${ws(id)}/offer-term-templates/${templateId}`,

    /// 5.9 job offers.
    jobOffers: (id: string) => `${ws(id)}/job-offers`,
    jobOffer: (id: string, offerId: string) => `${ws(id)}/job-offers/${offerId}`,
    jobOfferRecordResponse: (id: string, offerId: string) =>
      `${ws(id)}/job-offers/${offerId}/record-response`,

    /// 5.11 employee referrals. `scope` (own | workspace) is a query param.
    employeeReferrals: (id: string) => `${ws(id)}/employee-referrals`,
    employeeReferral: (id: string, referralId: string) =>
      `${ws(id)}/employee-referrals/${referralId}`,

    /// 5.12 appointment letters. Letters are immutable once generated, so
    /// there is deliberately no PATCH or DELETE builder for one.
    appointmentLetterTemplates: (id: string) => `${ws(id)}/appointment-letter-templates`,
    appointmentLetterTemplate: (id: string, templateId: string) =>
      `${ws(id)}/appointment-letter-templates/${templateId}`,
    appointmentLetters: (id: string) => `${ws(id)}/appointment-letters`,

    /// 5.13 funnel report and its CSV stream.
    recruitmentFunnel: (id: string) => `${ws(id)}/recruitment-funnel`,
    recruitmentFunnelExportCsv: (id: string) => `${ws(id)}/recruitment-funnel/export.csv`,
  },

  payroll: {
    components: (id: string) => `${ws(id)}/salary-components`,
    component: (id: string, componentId: string) => `${ws(id)}/salary-components/${componentId}`,
    /// Always answers 200; a bad expression is the response body, not an error.
    validateFormula: (id: string) => `${ws(id)}/salary-components/validate-formula`,
    /// The member-facing projection of the component catalog (ESS-30): the
    /// live flexible benefits, gated on BENEFIT_VIEW_OWN.
    flexibleBenefitComponents: (id: string) => `${ws(id)}/salary-components/flexible-benefits`,
    structures: (id: string) => `${ws(id)}/salary-structures`,
    structure: (id: string, structureId: string, query?: { replacementStructureId?: string }) => {
      const path = `${ws(id)}/salary-structures/${structureId}`;
      if (!query?.replacementStructureId) return path;
      return `${path}?replacementStructureId=${encodeURIComponent(query.replacementStructureId)}`;
    },
    /// POST because the inputs are a body; it still persists nothing.
    structurePreview: (id: string, structureId: string) =>
      `${ws(id)}/salary-structures/${structureId}/preview`,
    /// CTC redesign: evaluate an UNSAVED draft (lines + annual CTC) so the
    /// structure editor can show live numbers before anything is saved.
    structurePreviewDraft: (id: string) => `${ws(id)}/salary-structures/preview`,
    /// PAY-006.
    assignments: (id: string) => `${ws(id)}/salary-structure-assignments`,
    /// CTC redesign. Declared before `assignment` for the same reason the
    /// shore declares them first: none of these is an assignment id.
    assignmentsBulk: (id: string) => `${ws(id)}/salary-structure-assignments/bulk`,
    assignmentsOverview: (id: string) => `${ws(id)}/salary-structure-assignments/overview`,
    assignmentsMe: (id: string) => `${ws(id)}/salary-structure-assignments/me`,
    assignmentPackagePreview: (id: string) =>
      `${ws(id)}/salary-structure-assignments/package/preview`,
    assignment: (id: string, assignmentId: string) =>
      `${ws(id)}/salary-structure-assignments/${assignmentId}`,
    assignmentPackage: (id: string, assignmentId: string) =>
      `${ws(id)}/salary-structure-assignments/${assignmentId}/package`,
    assignmentBreakup: (id: string, assignmentId: string) =>
      `${ws(id)}/salary-structure-assignments/${assignmentId}/breakup`,
    /// CTC redesign: the workspace payroll profile and the template engine.
    setup: (id: string) => `${ws(id)}/payroll-setup`,
    setupTemplates: (id: string) => `${ws(id)}/payroll-setup/templates`,
    setupTemplatePreview: (id: string, templateKey: string) =>
      `${ws(id)}/payroll-setup/templates/${templateKey}/preview`,
    setupApply: (id: string) => `${ws(id)}/payroll-setup/apply`,
    setupBootstrapFromStructure: (id: string) => `${ws(id)}/payroll-setup/bootstrap-from-structure`,
    setupStructures: (id: string) => `${ws(id)}/payroll-setup/structures`,
    setupProfile: (id: string) => `${ws(id)}/payroll-setup/profile`,
    setupAttendancePolicy: (id: string) => `${ws(id)}/payroll-setup/attendance-policy`,
    setupAttendancePolicyPublish: (id: string) =>
      `${ws(id)}/payroll-setup/attendance-policy/publish`,
    setupAttendancePolicySimulate: (id: string) =>
      `${ws(id)}/payroll-setup/attendance-policy/simulate`,
    setupStatutoryRules: (id: string) => `${ws(id)}/payroll-setup/statutory-rules`,
    /// Tax intelligence B-05. `:country` rides the preview path because the
    /// list row builds the link, but shore resolves the country from the
    /// payroll profile and ignores it: a caller-supplied country would let one
    /// workspace seed itself another country's tables.
    taxPacks: (id: string) => `${ws(id)}/tax-packs`,
    taxPackPreview: (id: string, country: string, taxYearKey: string) =>
      `${ws(id)}/tax-packs/${country}/${taxYearKey}/preview`,
    taxPackApply: (id: string) => `${ws(id)}/tax-packs/apply`,
    /// Tax intelligence D-07. Every route is `/me`: none carries a
    /// membershipId, so "read somebody else's estimate" is not a request the
    /// API can express. `period` rides the query string because it is a key;
    /// rent, age and amounts go in POST bodies (FE-SEC7).
    taxEstimateMe: (id: string, payrollPeriodId: string) =>
      `${ws(id)}/tax-estimates/me?payrollPeriodId=${encodeURIComponent(payrollPeriodId)}`,
    taxEstimateOptimise: (id: string) => `${ws(id)}/tax-estimates/me/optimise`,
    taxEstimateApply: (id: string) => `${ws(id)}/tax-estimates/me/apply`,
    /// Tax intelligence F-07. Aggregates only, never a member.
    structureGapDemand: (id: string) => `${ws(id)}/payroll-setup/structure-gap-demand`,
    periods: (id: string) => `${ws(id)}/payroll-periods`,
    /// The member-facing counterpart to `periods`, which is config gated.
    /// Every money document names a period, so a member needs this to file.
    periodsMe: (id: string) => `${ws(id)}/payroll-periods/me`,
    period: (id: string, periodId: string) => `${ws(id)}/payroll-periods/${periodId}`,
    /// ESS-31. One election per person per tax year: GET reads it (or the
    /// statutory default), PUT sets it. No membershipId in either, because the
    /// target is always the caller.
    taxRegimeElections: (id: string) => `${ws(id)}/tax-regime-elections`,
    /// ESS-32. Seed the India pack into a workspace's own tables. Idempotent.
    taxExemptionCategoryPresets: (id: string) => `${ws(id)}/tax-exemption-categories/presets/india`,
    incomeTaxSlabPresets: (id: string) => `${ws(id)}/income-tax-slabs/presets/india`,
    taxSlabs: (id: string) => `${ws(id)}/income-tax-slabs`,
    taxSlab: (id: string, slabId: string) => `${ws(id)}/income-tax-slabs/${slabId}`,
    /// PAY-016. One collection, two lenses: `?scope=mine` (the default) answers
    /// the caller's own RELEASED slips, `?scope=workspace` needs
    /// SALARY_SLIP_VIEW_WORKSPACE and additionally accepts `payrollRunId`.
    salarySlips: (id: string) => `${ws(id)}/salary-slips`,
    salarySlip: (id: string, salarySlipId: string) => `${ws(id)}/salary-slips/${salarySlipId}`,
    /// PAY-024 admin transitions. All three need SALARY_SLIP_MANAGE_WORKSPACE
    /// and take `{ version }`; the shore answers 409 on a stale version.
    salarySlipSubmit: (id: string, salarySlipId: string) =>
      `${ws(id)}/salary-slips/${salarySlipId}/submit`,
    salarySlipMarkPaid: (id: string, salarySlipId: string) =>
      `${ws(id)}/salary-slips/${salarySlipId}/mark-paid`,
    salarySlipCancel: (id: string, salarySlipId: string) =>
      `${ws(id)}/salary-slips/${salarySlipId}/cancel`,

    /// PAY-020 and PAY-021, the admin run wizard. Under `admin/` unlike the
    /// member `salarySlips` above: every one of these is the workspace lens on
    /// payroll and carries PAYROLL_RUN_VIEW_WORKSPACE or MANAGE_WORKSPACE.
    runs: (id: string) => `${ws(id)}/admin/payroll-runs`,
    run: (id: string, runId: string) => `${ws(id)}/admin/payroll-runs/${runId}`,
    /// Recomputed on every call and persists nothing, so the wizard's review
    /// step always resolves against today's roster.
    runEmployees: (id: string, runId: string) => `${ws(id)}/admin/payroll-runs/${runId}/employees`,
    runPaymentDays: (id: string, runId: string) =>
      `${ws(id)}/admin/payroll-runs/${runId}/payment-days`,
    runPaymentDayDetail: (id: string, runId: string, membershipId: string) =>
      `${ws(id)}/admin/payroll-runs/${runId}/payment-days/${membershipId}`,
    runAttendanceReadiness: (id: string, runId: string) =>
      `${ws(id)}/admin/payroll-runs/${runId}/attendance-readiness`,
    runAttendanceDays: (id: string, runId: string) =>
      `${ws(id)}/admin/payroll-runs/${runId}/attendance-days`,
    runAttendanceReviewConfirm: (id: string, runId: string) =>
      `${ws(id)}/admin/payroll-runs/${runId}/attendance-review/confirm`,
    runAttendanceReviewAmend: (id: string, runId: string) =>
      `${ws(id)}/admin/payroll-runs/${runId}/attendance-review/amendments`,
    runDatedPaymentDaysOverrides: (
      id: string,
      runId: string,
      membershipId: string,
      workDate: string,
    ) => `${ws(id)}/admin/payroll-runs/${runId}/payment-days-overrides/${membershipId}/${workDate}`,
    corrections: (id: string) => `${ws(id)}/payroll-corrections`,
    correctionObligations: (id: string) => `${ws(id)}/payroll-corrections/obligations`,
    correction: (id: string, correctionId: string) =>
      `${ws(id)}/payroll-corrections/${correctionId}`,
    correctionApprove: (id: string, correctionId: string) =>
      `${ws(id)}/payroll-corrections/${correctionId}/approve`,
    correctionRetry: (id: string, correctionId: string) =>
      `${ws(id)}/payroll-corrections/${correctionId}/retry`,
    /// PAYD-FR-085. Cycle-scoped payroll days, not a rewrite of leave facts.
    paymentDaysOverrides: (id: string, cycleStart: string) =>
      `${ws(id)}/payment-days-overrides/${cycleStart}`,
    paymentDaysOverride: (id: string, cycleStart: string, membershipId: string) =>
      `${ws(id)}/payment-days-overrides/${cycleStart}/${membershipId}`,
    runPrepareCompare: (id: string, runId: string) =>
      `${ws(id)}/admin/payroll-runs/${runId}/prepare-compare`,
    /// The per-member report body. Separate from the run document because the
    /// document is POLLED while a run drains and a 500-row report on every
    /// poll would make the cheap read the expensive one.
    runFailureReport: (id: string, runId: string) =>
      `${ws(id)}/admin/payroll-runs/${runId}/failure-report`,
    runProcess: (id: string, runId: string) => `${ws(id)}/admin/payroll-runs/${runId}/process`,
    /// ESS-04. Tell the people in this run who cannot be paid. Idempotent on
    /// the shore, so a second press reports what was already sent.
    runRemindMissingDetails: (id: string, runId: string) =>
      `${ws(id)}/admin/payroll-runs/${runId}/remind-missing-details`,
    runSubmit: (id: string, runId: string) => `${ws(id)}/admin/payroll-runs/${runId}/submit`,
    runMarkPaid: (id: string, runId: string) => `${ws(id)}/admin/payroll-runs/${runId}/mark-paid`,
    runCancel: (id: string, runId: string) => `${ws(id)}/admin/payroll-runs/${runId}/cancel`,
    /// PAYROLL_BANK_EXPORT, its own rate limit, its own audit row: the only
    /// place account numbers leave the database as values. Streams text/csv,
    /// so the browser fetches it through a plain anchor, not `apiCall`.
    /// `?paymentDate=yyyy-mm-dd` is REQUIRED (shore `BankExportQueryDto`).
    runBankExportCsv: (id: string, runId: string) =>
      `${ws(id)}/admin/payroll-runs/${runId}/bank-export.csv`,

    /// C-03 (PAYD-FR-020, FR-022). The run's editable roster: who is in it and
    /// what payroll should DO with each of them. On the run prefix rather than
    /// a resource of its own, because a roster row has no life outside its run.
    /// Distinct from `runEmployees` above, which is the recomputed PREVIEW and
    /// persists nothing; this one is the stored rows the worker drains.
    runRoster: (id: string, runId: string) => `${ws(id)}/admin/payroll-runs/${runId}/roster`,
    runRosterMember: (id: string, runId: string, membershipId: string) =>
      `${ws(id)}/admin/payroll-runs/${runId}/roster/${membershipId}`,
    runRosterMemberReprocess: (id: string, runId: string, membershipId: string) =>
      `${ws(id)}/admin/payroll-runs/${runId}/roster/${membershipId}/reprocess`,

    /// C-06 and C-07 (PAYD-FR-025). What actually left for the bank, per file.
    /// `reissue` is declared before the per-file routes for the same reason the
    /// shore declares it first: otherwise "reissue" reads as a file id.
    runPaymentFiles: (id: string, runId: string) =>
      `${ws(id)}/admin/payroll-runs/${runId}/payment-files`,
    runPaymentFileReissue: (id: string, runId: string) =>
      `${ws(id)}/admin/payroll-runs/${runId}/payment-files/reissue`,
    runPaymentFileMarkPaid: (id: string, runId: string, paymentFileId: string) =>
      `${ws(id)}/admin/payroll-runs/${runId}/payment-files/${paymentFileId}/mark-paid`,

    /// C-11 (PAYD-FR-089, FR-090, FR-139). The wizard's variance step.
    /// `acknowledge-bulk` before `:flagId`, again because Nest matches in
    /// declaration order and the other way round it would be read as an id.
    runVariance: (id: string, runId: string) => `${ws(id)}/admin/payroll-runs/${runId}/variance`,
    runVarianceAcknowledgeBulk: (id: string, runId: string) =>
      `${ws(id)}/admin/payroll-runs/${runId}/variance/acknowledge-bulk`,
    runVarianceAcknowledge: (id: string, runId: string, flagId: string) =>
      `${ws(id)}/admin/payroll-runs/${runId}/variance/${flagId}/acknowledge`,
    runVarianceFix: (id: string, runId: string, flagId: string) =>
      `${ws(id)}/admin/payroll-runs/${runId}/variance/${flagId}/fix`,
    runPreflight: (id: string, runId: string) => `${ws(id)}/admin/payroll-runs/${runId}/preflight`,
    runPreflightRecompute: (id: string, runId: string) =>
      `${ws(id)}/admin/payroll-runs/${runId}/preflight/recompute`,
    runPreflightDismiss: (id: string, runId: string, findingId: string) =>
      `${ws(id)}/admin/payroll-runs/${runId}/preflight/${findingId}/dismiss`,
    runPreflightAction: (id: string, runId: string, findingId: string) =>
      `${ws(id)}/admin/payroll-runs/${runId}/preflight/${findingId}/action`,

    /// C-07. The bank sent one member's transfer back. PATCH on a sub-resource
    /// rather than a `/fail` action: the slip is not moving through its own
    /// lifecycle, a FIELD of it is being corrected to match what the bank said.
    salarySlipPaymentStatus: (id: string, salarySlipId: string) =>
      `${ws(id)}/salary-slips/${salarySlipId}/payment-status`,

    /// Workstream B (PAYD-FR-081, FR-143). One row per cycle: cut-off date,
    /// planned pay date and how far the cycle has been closed. `GET` CREATES
    /// the row at its defaults, which is why there is no POST here.
    cycleControl: (id: string, cycleStart: string) =>
      `${ws(id)}/payroll-cycle-controls/${cycleStart}`,
    cycleControlUnlock: (id: string, cycleStart: string) =>
      `${ws(id)}/payroll-cycle-controls/${cycleStart}/unlock`,

    /// PAY-014 tax self-service. The catalog these price against
    /// (`taxExemptionCategories`) is PAYROLL_CONFIG_VIEW gated, unlike the
    /// three member surfaces under it, which is why the money section can read
    /// somebody's declaration but cannot always offer them a new category.
    taxExemptionCategories: (id: string) => `${ws(id)}/tax-exemption-categories`,
    taxExemptionCategory: (id: string, categoryId: string) =>
      `${ws(id)}/tax-exemption-categories/${categoryId}`,
    /// The member-facing projection of the same catalog (ESS-30). Four fields,
    /// gated on TAX_DECLARATION_VIEW_OWN, so the money section can offer a
    /// category to somebody who cannot see payroll setup.
    taxExemptionCategoriesAvailable: (id: string) => `${ws(id)}/tax-exemption-categories/available`,
    taxExemptionDeclarations: (id: string) => `${ws(id)}/tax-exemption-declarations`,
    taxExemptionDeclaration: (id: string, declarationId: string) =>
      `${ws(id)}/tax-exemption-declarations/${declarationId}`,
    taxExemptionProofs: (id: string) => `${ws(id)}/tax-exemption-proofs`,
    /// Deciding is TAX_DECLARATION_MANAGE_WORKSPACE; filing above is not.
    taxExemptionProofVerify: (id: string, proofId: string) =>
      `${ws(id)}/tax-exemption-proofs/${proofId}/verify`,
    taxExemptionProofReject: (id: string, proofId: string) =>
      `${ws(id)}/tax-exemption-proofs/${proofId}/reject`,
    employeeOtherIncomes: (id: string) => `${ws(id)}/employee-other-incomes`,
    employeeOtherIncome: (id: string, otherIncomeId: string) =>
      `${ws(id)}/employee-other-incomes/${otherIncomeId}`,

    /// PAY-015 flexible benefits. Three resources on one controller: the plan,
    /// the claims against it, and the ledger that reconciles them.
    benefitApplications: (id: string) => `${ws(id)}/benefit-applications`,
    benefitApplicationCancel: (id: string, applicationId: string) =>
      `${ws(id)}/benefit-applications/${applicationId}/cancel`,
    benefitClaims: (id: string) => `${ws(id)}/benefit-claims`,
    benefitClaimCancel: (id: string, benefitClaimId: string) =>
      `${ws(id)}/benefit-claims/${benefitClaimId}/cancel`,
    /// The step that actually moves money: BENEFIT_MANAGE_WORKSPACE.
    benefitClaimMarkPaid: (id: string, benefitClaimId: string) =>
      `${ws(id)}/benefit-claims/${benefitClaimId}/mark-paid`,
    /// Answers `{ rows, nextCursor, balances }`. The balances are on the DATA
    /// payload, not on `meta`, and are summed from the rows on every read.
    benefitLedger: (id: string) => `${ws(id)}/benefit-ledger`,
  },

  /**
   * Payroll depth D and E: the payroll inputs page and everything it writes.
   *
   * Its own group rather than entries under `payroll`, because the page is one
   * screen served by three shore controllers on two prefixes: the cycle read
   * and the named overrides sit on `payroll-inputs`, and the row writes sit on
   * `additional-salaries`, which the member-facing pay details also calls.
   *
   * `forCycle` takes the cycle START (`YYYY-MM-DD`) as a path segment, which is
   * how `PayrollInputsController` declares it; the filters ride a query string
   * built at the call site, the convention the rest of this file keeps.
   */
  payrollInputs: {
    forCycle: (id: string, cycleStart: string) => `${ws(id)}/payroll-inputs/${cycleStart}`,
    overrides: (id: string, cycleStart: string) =>
      `${ws(id)}/payroll-inputs/${cycleStart}/overrides`,
    rows: (id: string) => `${ws(id)}/additional-salaries`,
    row: (id: string, rowId: string) => `${ws(id)}/additional-salaries/${rowId}`,
    cancelRow: (id: string, rowId: string) => `${ws(id)}/additional-salaries/${rowId}/cancel`,
  },

  /**
   * Payroll depth E: instalment recoveries, salary advances and court orders.
   *
   * `instalments` is a POST that opens one cycle's instalments across every
   * live schedule, so it is declared before `detail` for the reason the shore
   * declares it first: "instalments" is not a schedule id.
   */
  recoverySchedules: {
    list: (id: string) => `${ws(id)}/recovery-schedules`,
    instalments: (id: string) => `${ws(id)}/recovery-schedules/instalments`,
    detail: (id: string, scheduleId: string) => `${ws(id)}/recovery-schedules/${scheduleId}`,
    pause: (id: string, scheduleId: string) => `${ws(id)}/recovery-schedules/${scheduleId}/pause`,
    resume: (id: string, scheduleId: string) => `${ws(id)}/recovery-schedules/${scheduleId}/resume`,
    settle: (id: string, scheduleId: string) => `${ws(id)}/recovery-schedules/${scheduleId}/settle`,
    cancel: (id: string, scheduleId: string) => `${ws(id)}/recovery-schedules/${scheduleId}/cancel`,
  },

  /// Payroll depth E: the arrears calculator for a backdated revision. `list`
  /// doubles as the compute POST, which is the shore's own shape.
  arrears: {
    list: (id: string) => `${ws(id)}/arrears`,
    detail: (id: string, computationId: string) => `${ws(id)}/arrears/${computationId}`,
    apply: (id: string, computationId: string) => `${ws(id)}/arrears/${computationId}/apply`,
    cancel: (id: string, computationId: string) => `${ws(id)}/arrears/${computationId}/cancel`,
  },

  /**
   * Payroll depth G: the full and final settlement worksheet (PAYD-FR-030 to
   * FR-034, FR-110, FR-113).
   *
   * Every WORKSHEET route here is gated on FNF_MANAGE_WORKSPACE. `me` is the
   * exception and the only own-scope one: FNF_VIEW_OWN, which every human role
   * holds, answering "which settlement is mine" for the person it is about.
   * It exists because G-11 made all five statement routes readable by their
   * subject but every one of them takes an id that nothing own-scope handed
   * out. `me` is a static segment and the shore's router resolves it ahead of
   * `:id`, the same way `leave.balancesMe` sits beside `leave.period`.
   *
   * `statementAcknowledge` and `statementDispute` are the two WRITES on that
   * statement, and the only writes anywhere in this catalog a former employee
   * can reach: the shore tagged them `@AlumniAllowed()` alongside the reads,
   * because the subject of a settlement has by definition left. The statement
   * model, print and PDF routes are still not builders here; nothing in river
   * calls them.
   *
   * `gratuityLine` and `encashmentLine` are declared before `waiveLine`
   * because "encashment" and "gratuity" are not line ids, the same ordering
   * the shore controller keeps.
   */
  settlements: {
    list: (id: string) => `${ws(id)}/settlements`,
    /// PAYD-FR-033 (I-13). The caller's own settlements. FNF_VIEW_OWN, and
    /// reachable by a leaver through the alumni window.
    me: (id: string) => `${ws(id)}/settlements/me`,
    /// The subject accepting the statement they were sent, and the subject
    /// saying a figure on it is wrong. FNF_VIEW_OWN, alumni allowed.
    statementAcknowledge: (id: string, settlementId: string) =>
      `${ws(id)}/settlements/${settlementId}/statement/acknowledge`,
    statementDispute: (id: string, settlementId: string) =>
      `${ws(id)}/settlements/${settlementId}/statement/dispute`,
    detail: (id: string, settlementId: string) => `${ws(id)}/settlements/${settlementId}`,
    lines: (id: string, settlementId: string) => `${ws(id)}/settlements/${settlementId}/lines`,
    encashmentLine: (id: string, settlementId: string) =>
      `${ws(id)}/settlements/${settlementId}/lines/encashment`,
    gratuityLine: (id: string, settlementId: string) =>
      `${ws(id)}/settlements/${settlementId}/lines/gratuity`,
    waiveLine: (id: string, settlementId: string, lineId: string) =>
      `${ws(id)}/settlements/${settlementId}/lines/${lineId}/waive`,
    finalize: (id: string, settlementId: string) =>
      `${ws(id)}/settlements/${settlementId}/finalize`,
    cancel: (id: string, settlementId: string) => `${ws(id)}/settlements/${settlementId}/cancel`,
  },

  /**
   * HRMS-PAY-030. Admin hold list, place, release, and cancel. River only
   * releases from the run workspace; create stays available for a later slice.
   */
  salaryWithholdings: {
    list: (id: string) => `${ws(id)}/salary-withholdings`,
    release: (id: string, withholdingId: string) =>
      `${ws(id)}/salary-withholdings/${withholdingId}/release`,
    cancel: (id: string, withholdingId: string) =>
      `${ws(id)}/salary-withholdings/${withholdingId}/cancel`,
  },

  /**
   * Payroll depth I: statutory compliance documents (PAYD-FR-050 to FR-065).
   *
   * Every RUN route is gated on COMPLIANCE_DOCUMENT_MANAGE_WORKSPACE. The
   * three `me` builders are the employee-facing half (I-13): their own
   * published certificates, one of them, and a download.
   *
   * `meDownload` does NOT return the file. It answers a short-lived signed
   * object-storage URL, which the browser then follows; treat it as a link to
   * fetch fresh at click time, never one to cache in component state past its
   * `expiresAt`. Whether the workspace's storage can actually serve those
   * bytes is a deployment question (the private bucket must be configured);
   * an unconfigured one answers 503 rather than a dead link.
   *
   * `partA` and `signed` take a multipart body with a single file field named
   * "zip"; every other route is JSON.
   */
  complianceDocs: {
    runs: (id: string) => `${ws(id)}/compliance-docs/runs`,
    documentTypes: (id: string) => `${ws(id)}/compliance-docs/document-types`,
    me: (id: string) => `${ws(id)}/compliance-docs/me`,
    meDocument: (id: string, documentId: string) => `${ws(id)}/compliance-docs/me/${documentId}`,
    meDownload: (id: string, documentId: string) =>
      `${ws(id)}/compliance-docs/me/${documentId}/download`,
    run: (id: string, runId: string) => `${ws(id)}/compliance-docs/runs/${runId}`,
    runDocuments: (id: string, runId: string) =>
      `${ws(id)}/compliance-docs/runs/${runId}/documents`,
    runDocumentDownload: (id: string, runId: string, documentId: string) =>
      `${ws(id)}/compliance-docs/runs/${runId}/documents/${documentId}/download`,
    generate: (id: string, runId: string) => `${ws(id)}/compliance-docs/runs/${runId}/generate`,
    regenerate: (id: string, runId: string, taxIdentity: string) =>
      `${ws(id)}/compliance-docs/runs/${runId}/documents/${encodeURIComponent(taxIdentity)}/regenerate`,
    publish: (id: string, runId: string) => `${ws(id)}/compliance-docs/runs/${runId}/publish`,
    signatory: (id: string, runId: string) => `${ws(id)}/compliance-docs/runs/${runId}/signatory`,
    partA: (id: string, runId: string) => `${ws(id)}/compliance-docs/runs/${runId}/part-a`,
    signed: (id: string, runId: string) => `${ws(id)}/compliance-docs/runs/${runId}/signed`,
    consent: (id: string) => `${ws(id)}/compliance-docs/consent`,
    consentWithdraw: (id: string) => `${ws(id)}/compliance-docs/consent/withdraw`,
  },

  /// /workspaces/:wsId/payroll-lock/:membershipId (B-12 payroll cycle lock).
  /// GET only, answers `{ rows: LockedCycleDto[] }` for the given member over
  /// a `from`/`to` window. Query string stays at the call site (RT2 note at
  /// the top of this file), same convention as `audit` and `approvals` below.
  payrollLock: {
    forMembership: (id: string, membershipId: string) => `${ws(id)}/payroll-lock/${membershipId}`,
  },

  /// PAYD-FR-010 to FR-018 (payroll depth F-08). Variable pay: the plan is the
  /// rule, the payout is one generated batch of that rule, and a line is one
  /// member inside the batch. The batch routes nest under the plan because
  /// shore checks the pair: a batch id from another plan answers 404 rather
  /// than somebody else's numbers.
  ///
  /// `me` is the only member-facing surface and carries no membership id, so
  /// "read a colleague's variable pay" is not a request this API can express.
  variablePay: {
    plans: (id: string) => `${ws(id)}/variable-pay-plans`,
    audiencePreview: (id: string) => `${ws(id)}/variable-pay-plans/audience-preview`,
    plan: (id: string, planId: string) => `${ws(id)}/variable-pay-plans/${planId}`,
    /// POST, and the only way to `archived`: it takes a reason and keeps every
    /// batch the plan already generated.
    planArchive: (id: string, planId: string) => `${ws(id)}/variable-pay-plans/${planId}/archive`,
    accrual: (id: string, planId: string) => `${ws(id)}/variable-pay-plans/${planId}/accrual`,
    payouts: (id: string, planId: string) => `${ws(id)}/variable-pay-plans/${planId}/payouts`,
    payout: (id: string, planId: string, payoutId: string) =>
      `${ws(id)}/variable-pay-plans/${planId}/payouts/${payoutId}`,
    payoutLines: (id: string, planId: string, payoutId: string) =>
      `${ws(id)}/variable-pay-plans/${planId}/payouts/${payoutId}/lines`,
    payoutLine: (id: string, planId: string, payoutId: string, lineId: string) =>
      `${ws(id)}/variable-pay-plans/${planId}/payouts/${payoutId}/lines/${lineId}`,
    payoutLineCancel: (id: string, planId: string, payoutId: string, lineId: string) =>
      `${ws(id)}/variable-pay-plans/${planId}/payouts/${payoutId}/lines/${lineId}/cancel`,
    payoutApprove: (id: string, planId: string, payoutId: string) =>
      `${ws(id)}/variable-pay-plans/${planId}/payouts/${payoutId}/approve`,
    payoutCancel: (id: string, planId: string, payoutId: string) =>
      `${ws(id)}/variable-pay-plans/${planId}/payouts/${payoutId}/cancel`,
    payoutMaterialisePreview: (id: string, planId: string, payoutId: string) =>
      `${ws(id)}/variable-pay-plans/${planId}/payouts/${payoutId}/materialise-preview`,
    payoutMaterialise: (id: string, planId: string, payoutId: string) =>
      `${ws(id)}/variable-pay-plans/${planId}/payouts/${payoutId}/materialise`,
    payoutLinesBulkAction: (id: string, planId: string, payoutId: string) =>
      `${ws(id)}/variable-pay-plans/${planId}/payouts/${payoutId}/lines/bulk-action`,
    /// FR-018, the member's own card. Approved batches and `pay` lines only.
    me: (id: string) => `${ws(id)}/variable-pay/me`,
  },

  /// PAYD-FR-138 (payroll depth L-02, L-03). Two shore controllers, one group
  /// here because an admin closing a run reaches for both in the same sitting:
  /// the evidence pack that closes it out, and the review actions that clear
  /// its findings a chip or a code at a time.
  ///
  /// `closurePack` and `memberMoneyAuditCsv` stream a file (ZIP and CSV), so
  /// the browser fetches them through a plain anchor, not `apiCall`.
  payrollAudit: {
    closurePack: (id: string, payrollRunId: string) =>
      `${ws(id)}/payroll-reports/closure-pack/${payrollRunId}`,
    /// `?membershipId=&from=&to=` are all REQUIRED (shore
    /// `MemberMoneyAuditQueryDto`), and the window is capped at 366 days.
    memberMoneyAudit: (id: string) => `${ws(id)}/payroll-reports/member-money-audit`,
    memberMoneyAuditCsv: (id: string) => `${ws(id)}/payroll-reports/member-money-audit/export.csv`,
    attendanceToPay: (id: string, payrollRunId: string) =>
      `${ws(id)}/payroll-reports/attendance-to-pay/${payrollRunId}`,
    attendanceToPayCsv: (id: string, payrollRunId: string) =>
      `${ws(id)}/payroll-reports/attendance-to-pay/${payrollRunId}/export.csv`,
    /// One chip per call and one code per call by design on the shore: each
    /// bulk action leaves its own audit row saying which chip covered which
    /// rows, rather than one row that hides it.
    bulkAcknowledgeVariance: (id: string) => `${ws(id)}/payroll/bulk-ops/variance/acknowledge`,
    bulkDismissFindings: (id: string) => `${ws(id)}/payroll/bulk-ops/preflight/dismiss`,
    /// Takes an explicit list of slip ids (max 200 on the shore): a targeted
    /// resend after a bounce, never "blast the whole run again".
    bulkResendSlipNotifications: (id: string) => `${ws(id)}/payroll/bulk-ops/notifications/resend`,
  },

  /// PAYD-FR-100 to FR-103 (payroll depth K-05). The legacy register import
  /// and the comparison it feeds. Two shore controllers, one group here: an
  /// engineer holds an import id and everything else hangs off it.
  parallelRun: {
    imports: (id: string) => `${ws(id)}/parallel-run-imports`,
    import: (id: string, importId: string) => `${ws(id)}/parallel-run-imports/${importId}`,
    importRows: (id: string, importId: string) => `${ws(id)}/parallel-run-imports/${importId}/rows`,
    /// PUT. Re-maps an already uploaded register (component columns are mapped
    /// here, after the upload has reported which headers it did not recognise).
    importMapping: (id: string, importId: string) =>
      `${ws(id)}/parallel-run-imports/${importId}/mapping`,
    compare: (id: string, importId: string) => `${ws(id)}/parallel-run-imports/${importId}/compare`,
    comparison: (id: string, importId: string) =>
      `${ws(id)}/parallel-run-imports/${importId}/comparison`,
    /// Keyed by COMPARISON id, not import id: a re-run makes a new comparison
    /// and the old variances stay attached to the one they were found in.
    variances: (id: string, comparisonId: string) =>
      `${ws(id)}/parallel-run-comparisons/${comparisonId}/variances`,
    variance: (id: string, varianceId: string) => `${ws(id)}/parallel-run-variances/${varianceId}`,
    /// The notes already written in this workspace, so the tenth "PF rounds
    /// down in the legacy system" is picked rather than retyped.
    varianceNotes: (id: string) => `${ws(id)}/parallel-run-variance-notes`,
  },

  leave: {
    coverageProposals: (id: string) => `${ws(id)}/leave/coverage-proposals`,
    coverageContext: (id: string) => `${ws(id)}/leave/coverage-proposals/context`,
    coverageProposalConfirm: (id: string, applicationId: string) =>
      `${ws(id)}/leave/coverage-proposals/${applicationId}/confirm`,
    /// The six read surfaces (task-17-report.md).
    balancesMe: (id: string) => `${ws(id)}/leave/balances/me`,
    applicationsMe: (id: string) => `${ws(id)}/leave/applications/me`,
    /// The leave years the caller may look at. A MEMBER route despite sitting
    /// under the same prefix as the `LEAVE_CONFIGURE` gated `period(...)`
    /// below; the shore serves the static `me` segment ahead of `:periodId`.
    periodsMe: (id: string) => `${ws(id)}/leave/periods/me`,
    balances: (id: string) => `${ws(id)}/leave/balances`,
    register: (id: string) => `${ws(id)}/leave-register`,
    registerExportCsv: (id: string) => `${ws(id)}/leave-register/export.csv`,
    /// The headline band. LEAVE_VIEW_SUBTREE, unlike the table beside it.
    balancesSummary: (id: string) => `${ws(id)}/leave/balances/summary`,
    calendar: (id: string) => `${ws(id)}/leave/calendar`,
    outToday: (id: string) => `${ws(id)}/leave/out-today`,
    ledger: (id: string) => `${ws(id)}/leave/ledger`,
    /// Applying (APP-1, APP-2). The preview is a SIBLING of the collection,
    /// not a member of it: it answers "what would filing this cost" and
    /// creates nothing, so it is `/leave/preview` and not
    /// `/leave/applications/preview` (`ApplicationController`'s own note).
    preview: (id: string) => `${ws(id)}/leave/preview`,
    applications: (id: string) => `${ws(id)}/leave/applications`,
    applicationsForMember: (id: string) => `${ws(id)}/leave/applications/for-member`,
    /// APR-2. The decider's queue and the decision itself, both LEAVE_DECIDE.
    /// How WIDE the queue is comes from the caller's permissions, never from a
    /// parameter, so there is nothing to pass here.
    applicationsToDecide: (id: string) => `${ws(id)}/leave/applications/to-decide`,
    applicationDecide: (id: string, applicationId: string) =>
      `${ws(id)}/leave/applications/${applicationId}/decide`,
    /// The one action a person can take on their own pending request
    /// (LEAVE_APPLY, same permission as filing it).
    applicationCancel: (id: string, applicationId: string) =>
      `${ws(id)}/leave/applications/${applicationId}/cancel`,
    compensatoryRequests: (id: string) => `${ws(id)}/leave/compensatory-requests`,
    compensatoryToDecide: (id: string) => `${ws(id)}/leave/compensatory-requests/to-decide`,
    compensatoryDecision: (id: string, requestId: string, decision: "approve" | "reject") =>
      `${ws(id)}/leave/compensatory-requests/${requestId}/${decision}`,
    allocations: (id: string) => `${ws(id)}/leave/allocations`,
    allocation: (id: string, allocationId: string) => `${ws(id)}/leave/allocations/${allocationId}`,
    allocationCancel: (id: string, allocationId: string) =>
      `${ws(id)}/leave/allocations/${allocationId}/cancel`,
    adjustments: (id: string) => `${ws(id)}/leave/adjustments`,
    adjustmentReverse: (id: string, entryId: string) =>
      `${ws(id)}/leave/adjustments/${entryId}/reverse`,
    /// Configuration (LEAVE_CONFIGURE / LEAVE_ALLOCATE, admin only).
    /// Whether leave is ready to run, computed by the shore so Hand, MCP and
    /// this screen read one answer. LEAVE_CONFIGURE.
    setupStatus: (id: string) => `${ws(id)}/leave/setup-status`,
    types: (id: string) => `${ws(id)}/leave/types`,
    type: (id: string, typeId: string) => `${ws(id)}/leave/types/${typeId}`,
    periods: (id: string) => `${ws(id)}/leave/periods`,
    period: (id: string, periodId: string) => `${ws(id)}/leave/periods/${periodId}`,
    periodActivate: (id: string, periodId: string) =>
      `${ws(id)}/leave/periods/${periodId}/activate`,
    yearSettings: (id: string) => `${ws(id)}/leave/year-settings`,
    yearEndOverview: (id: string) => `${ws(id)}/leave/year-end`,
    yearEndRuns: (id: string) => `${ws(id)}/leave/year-end/runs`,
    yearEndRun: (id: string, runId: string) => `${ws(id)}/leave/year-end/runs/${runId}`,
    yearEndPendingRequests: (id: string, runId: string) =>
      `${ws(id)}/leave/year-end/runs/${runId}/pending-requests`,
    yearEndPlan: (id: string, runId: string) => `${ws(id)}/leave/year-end/runs/${runId}/plan`,
    yearEndOverrides: (id: string, runId: string) =>
      `${ws(id)}/leave/year-end/runs/${runId}/overrides`,
    yearEndCommit: (id: string, runId: string) => `${ws(id)}/leave/year-end/runs/${runId}/commit`,
    yearEndRollback: (id: string, runId: string) =>
      `${ws(id)}/leave/year-end/runs/${runId}/rollback`,
    policies: (id: string) => `${ws(id)}/leave/policies`,
    policyCompose: (id: string) => `${ws(id)}/leave/policies/compose`,
    policyCoverage: (id: string, leavePeriodId: string) =>
      `${ws(id)}/leave/policies/coverage?leavePeriodId=${encodeURIComponent(leavePeriodId)}`,
    policy: (id: string, policyId: string) => `${ws(id)}/leave/policies/${policyId}`,
    policyAssignments: (id: string) => `${ws(id)}/leave/policy-assignments`,
    policyAssignmentsBulkPreview: (id: string) => `${ws(id)}/leave/policy-assignments/bulk/preview`,
    policyAssignmentsBulk: (id: string) => `${ws(id)}/leave/policy-assignments/bulk`,
    policyAssignment: (id: string, assignmentId: string) =>
      `${ws(id)}/leave/policy-assignments/${assignmentId}`,
    policyAssignmentEnd: (id: string, assignmentId: string) =>
      `${ws(id)}/leave/policy-assignments/${assignmentId}/end`,
    blockLists: (id: string) => `${ws(id)}/leave/block-lists`,
    blockList: (id: string, blockListId: string) => `${ws(id)}/leave/block-lists/${blockListId}`,
    blockListDates: (id: string, blockListId: string) =>
      `${ws(id)}/leave/block-lists/${blockListId}/dates`,
    blockListDate: (id: string, blockListId: string, dateId: string) =>
      `${ws(id)}/leave/block-lists/${blockListId}/dates/${dateId}`,
    blockListMembers: (id: string, blockListId: string) =>
      `${ws(id)}/leave/block-lists/${blockListId}/members`,
    blockListMember: (id: string, blockListId: string, memberId: string) =>
      `${ws(id)}/leave/block-lists/${blockListId}/members/${memberId}`,
  },

  /// /workspaces/:wsId/one-on-ones
  oneOnOnes: {
    list: (id: string) => `${ws(id)}/one-on-ones`,
    me: (id: string) => `${ws(id)}/one-on-ones/me`,
    detail: (id: string, oneOnOneId: string) => `${ws(id)}/one-on-ones/${oneOnOneId}`,
    actionItems: (id: string, oneOnOneId: string) =>
      `${ws(id)}/one-on-ones/${oneOnOneId}/action-items`,
    complete: (id: string, oneOnOneId: string) => `${ws(id)}/one-on-ones/${oneOnOneId}/complete`,
    cancel: (id: string, oneOnOneId: string) => `${ws(id)}/one-on-ones/${oneOnOneId}/cancel`,
    reschedule: (id: string, oneOnOneId: string) =>
      `${ws(id)}/one-on-ones/${oneOnOneId}/reschedule`,
    retryMaterializeNext: (id: string, oneOnOneId: string) =>
      `${ws(id)}/one-on-ones/${oneOnOneId}/retry-materialize-next`,
    prep: (id: string, oneOnOneId: string) => `${ws(id)}/one-on-ones/${oneOnOneId}/prep`,
    series: (id: string, seriesId: string) => `${ws(id)}/one-on-ones/series/${seriesId}`,
    cadence: (id: string) => `${ws(id)}/one-on-ones/cadence`,
    admin: (id: string) => `${ws(id)}/admin/one-on-ones`,
    adminCadence: (id: string) => `${ws(id)}/admin/one-on-ones/cadence`,
  },

  /// /workspaces/:wsId/one-on-ones/:id/agenda-items + flat /one-on-one-agenda-items
  agenda: {
    list: (id: string, oneOnOneId: string) => `${ws(id)}/one-on-ones/${oneOnOneId}/agenda-items`,
    item: (id: string, agendaItemId: string) => `${ws(id)}/one-on-one-agenda-items/${agendaItemId}`,
    toggleDiscussed: (id: string, agendaItemId: string) =>
      `${ws(id)}/one-on-one-agenda-items/${agendaItemId}/toggle-discussed`,
    reorder: (id: string, agendaItemId: string) =>
      `${ws(id)}/one-on-one-agenda-items/${agendaItemId}/reorder`,
  },

  /// /workspaces/:wsId/notifications
  notifications: {
    list: (id: string) => `${ws(id)}/notifications`,
    unreadCount: (id: string) => `${ws(id)}/notifications/unread-count`,
    read: (id: string, nId: string) => `${ws(id)}/notifications/${nId}/read`,
    markAllRead: (id: string) => `${ws(id)}/notifications/mark-all-read`,
    detail: (id: string, nId: string) => `${ws(id)}/notifications/${nId}`,
    preferences: (id: string) => `${ws(id)}/notifications/preferences`,
  },

  indicators: {
    list: (id: string) => `${ws(id)}/indicators`,
  },

  /// /workspaces/:wsId/pulse-surveys (+ /me/...)
  pulseSurveys: {
    list: (id: string) => `${ws(id)}/pulse-surveys`,
    mine: (id: string) => `${ws(id)}/pulse-surveys/mine`,
    myTeam: (id: string) => `${ws(id)}/pulse-surveys/my-team`,
    detail: (id: string, surveyId: string) => `${ws(id)}/pulse-surveys/${surveyId}`,
    runs: (id: string, surveyId: string) => `${ws(id)}/pulse-surveys/${surveyId}/runs`,
    runCancel: (id: string, surveyId: string, runId: string) =>
      `${ws(id)}/pulse-surveys/${surveyId}/runs/${runId}/cancel`,
    aiTestRun: (id: string, surveyId: string) => `${ws(id)}/pulse-surveys/${surveyId}/ai-test-run`,
    aiPulse: (id: string) => `${ws(id)}/pulse-surveys/ai-pulse`,
    runDetail: (id: string, surveyId: string, runId: string) =>
      `${ws(id)}/pulse-surveys/${surveyId}/runs/${runId}`,
    meSelfCheckIn: (id: string) => `${ws(id)}/pulse-surveys/me/self-check-in`,
    runInsights: (id: string, surveyId: string, runId: string) =>
      `${ws(id)}/pulse-surveys/${surveyId}/runs/${runId}/insights`,
    runResponses: (id: string, surveyId: string, runId: string) =>
      `${ws(id)}/pulse-surveys/${surveyId}/runs/${runId}/responses`,
    runWrittenAnswers: (id: string, surveyId: string, runId: string) =>
      `${ws(id)}/pulse-surveys/${surveyId}/runs/${runId}/written-answers`,
    answerThreadMessages: (id: string, answerId: string) =>
      `${ws(id)}/pulse-surveys/written-answers/${answerId}/thread/messages`,
    answerThreads: (id: string) => `${ws(id)}/pulse-surveys/answer-threads`,
    answerThread: (id: string, threadId: string) =>
      `${ws(id)}/pulse-surveys/answer-threads/${threadId}`,
    answerThreadResolve: (id: string, threadId: string) =>
      `${ws(id)}/pulse-surveys/answer-threads/${threadId}/resolve`,
    answerThreadClose: (id: string, threadId: string) =>
      `${ws(id)}/pulse-surveys/answer-threads/${threadId}/close`,
    answerThreadReopen: (id: string, threadId: string) =>
      `${ws(id)}/pulse-surveys/answer-threads/${threadId}/reopen`,
    meThreads: (id: string) => `${ws(id)}/pulse-surveys/me/threads`,
    meThread: (id: string, threadId: string) => `${ws(id)}/pulse-surveys/me/threads/${threadId}`,
    meThreadMessages: (id: string, threadId: string) =>
      `${ws(id)}/pulse-surveys/me/threads/${threadId}/messages`,
    meThreadReveal: (id: string, threadId: string) =>
      `${ws(id)}/pulse-surveys/me/threads/${threadId}/reveal`,
    /// Lifecycle action (activate/pause/archive) is interpolated by the caller.
    lifecycle: (id: string, surveyId: string, action: string) =>
      `${ws(id)}/pulse-surveys/${surveyId}/${action}`,
    resend: (id: string, surveyId: string) => `${ws(id)}/pulse-surveys/${surveyId}/resend`,
    mePending: (id: string) => `${ws(id)}/pulse-surveys/me/pending`,
    meHistory: (id: string) => `${ws(id)}/pulse-surveys/me/history`,
    meDelivery: (id: string, deliveryId: string) =>
      `${ws(id)}/pulse-surveys/me/deliveries/${deliveryId}/responses`,
    answerRepliesSetting: (id: string) => `${ws(id)}/pulse-surveys/answer-replies-setting`,
    meAnswerReplies: (id: string) => `${ws(id)}/pulse-surveys/me/answer-replies`,
    meDeliveryAnswerReplies: (id: string, deliveryId: string) =>
      `${ws(id)}/pulse-surveys/me/deliveries/${deliveryId}/answer-replies`,
  },

  /// /workspaces/:wsId/health-config
  healthConfig: (id: string) => `${ws(id)}/health-config`,

  /// /workspaces/:wsId/actions (+ /me, /:id/<verb>, /analytics)
  pulseActions: {
    list: (id: string) => `${ws(id)}/actions`,
    me: (id: string) => `${ws(id)}/actions/me`,
    updates: (id: string) => `${ws(id)}/action-updates`,
    detail: (id: string, actionId: string) => `${ws(id)}/actions/${actionId}`,
    /// Lifecycle verb: accept | start | complete | dismiss | blocked.
    transition: (id: string, actionId: string, verb: string) =>
      `${ws(id)}/actions/${actionId}/${verb}`,
    measure: (id: string, actionId: string) => `${ws(id)}/actions/${actionId}/measure`,
    close: (id: string, actionId: string) => `${ws(id)}/actions/${actionId}/close`,
    analytics: (id: string) => `${ws(id)}/actions/analytics`,
  },

  /// /workspaces/:wsId/workspace-dashboard
  dashboards: {
    workspace: (id: string) => `${ws(id)}/workspace-dashboard`,
    departmentDetail: (id: string, deptId: string) =>
      `${ws(id)}/workspace-dashboard/departments/${deptId}`,
    themes: (id: string) => `${ws(id)}/workspace-dashboard/themes`,
    themeDetail: (id: string, theme: string) =>
      `${ws(id)}/workspace-dashboard/themes/${encodeURIComponent(theme)}`,
    departmentComparison: (id: string) => `${ws(id)}/workspace-dashboard/department-comparison`,
  },

  /// /workspaces/:wsId/insights
  insights: {
    managerBrief: (id: string) => `${ws(id)}/insights/manager-brief`,
    workspaceBrief: (id: string) => `${ws(id)}/insights/workspace-brief`,
    queue: (id: string) => `${ws(id)}/insights/queue`,
    dismiss: (id: string, insightId: string) => `${ws(id)}/insights/${insightId}/dismiss`,
    review: (id: string, insightId: string) => `${ws(id)}/insights/${insightId}/review`,
    surveyRunReport: (id: string, runId: string) =>
      `${ws(id)}/insights/survey-runs/${runId}/report`,
    surveyRunReportGenerate: (id: string, runId: string) =>
      `${ws(id)}/insights/survey-runs/${runId}/report/generate`,
  },

  /// /workspaces/:wsId/pulse-scores
  scores: {
    own: (id: string) => `${ws(id)}/pulse-scores/own`,
    workspace: (id: string) => `${ws(id)}/pulse-scores/workspace`,
    department: (id: string, deptId: string) => `${ws(id)}/pulse-scores/departments/${deptId}`,
    managerSubtree: (id: string) => `${ws(id)}/pulse-scores/manager-subtree`,
  },

  /// /workspaces/:wsId/reputation (Module 13)
  reputation: {
    own: (id: string) => `${ws(id)}/reputation/own`,
    /// Batch read used by the People + My team table columns. Caller appends
    /// `?userIds=a,b,c`; the shore filters to ids the viewer can see.
    scores: (id: string) => `${ws(id)}/reputation/scores`,
    user: (id: string, userId: string) => `${ws(id)}/reputation/users/${userId}`,
  },

  /// /workspaces/:wsId/people (+ stats, detail, relationship-board)
  people: {
    list: (id: string) => `${ws(id)}/people`,
    stats: (id: string) => `${ws(id)}/people/stats`,
    /// Batched membership id to display name lookup. Unlike every roster read,
    /// this one answers for people who have LEFT (relieved and soft-deleted).
    /// Callers append `?ids=a,b,c` and must stay within `MEMBER_NAMES_MAX_IDS`.
    names: (id: string) => `${ws(id)}/people/names`,
    detail: (id: string, membershipId: string) => `${ws(id)}/people/${membershipId}`,
    relationship: (id: string, userId: string) => `${ws(id)}/people/${userId}/relationship`,
    relationshipSummary: (id: string, userId: string) =>
      `${ws(id)}/people/${userId}/relationship/summary`,
    relationshipKudos: (id: string, userId: string) =>
      `${ws(id)}/people/${userId}/relationship/kudos`,
    relationshipFeedback: (id: string, userId: string) =>
      `${ws(id)}/people/${userId}/relationship/feedback`,
    relationshipTodos: (id: string, userId: string) =>
      `${ws(id)}/people/${userId}/relationship/todos`,
    relationshipOneOnOnes: (id: string, userId: string) =>
      `${ws(id)}/people/${userId}/relationship/one-on-ones`,
    relationshipTimeline: (id: string, userId: string) =>
      `${ws(id)}/people/${userId}/relationship/timeline`,
    relationshipGoals: (id: string, userId: string) =>
      `${ws(id)}/people/${userId}/relationship/goals`,
  },

  /// /health-vocabulary/* (no workspace prefix)
  healthVocabulary: {
    dimensions: () => `/health-vocabulary/dimensions`,
    wedges: () => `/health-vocabulary/wedges`,
    weights: () => `/health-vocabulary/weights`,
  },

  /// /workspaces/:wsId/memberships (+ bulk, prefs, hr, manager)
  memberships: {
    mePrefs: (id: string) => `${ws(id)}/memberships/me/prefs`,
    prefs: (id: string, membershipId: string) => `${ws(id)}/memberships/${membershipId}/prefs`,
    hr: (id: string, membershipId: string) => `${ws(id)}/memberships/${membershipId}/hr`,
    manager: (id: string, membershipId: string) => `${ws(id)}/memberships/${membershipId}/manager`,
    bulkEditHr: (id: string) => `${ws(id)}/memberships/bulk-edit-hr`,
    bulkAssignManager: (id: string) => `${ws(id)}/memberships/bulk-assign-manager`,
    bulkChangeRole: (id: string) => `${ws(id)}/memberships/bulk-change-role`,
    bulkRemove: (id: string) => `${ws(id)}/memberships/bulk-remove`,
    myReports: (id: string) => `${ws(id)}/my-reports`,
    myReportsBulkEditPrefs: (id: string) => `${ws(id)}/my-reports/bulk-edit-prefs`,
  },

  /// /workspaces/:wsId/audience (the shared assignment picker's headcount)
  audience: {
    resolve: (id: string) => `${ws(id)}/audience/resolve`,
  },

  /// /workspaces/:wsId/departments
  departments: {
    list: (id: string) => `${ws(id)}/departments`,
    detail: (id: string, deptId: string) => `${ws(id)}/departments/${deptId}`,
    restore: (id: string, deptId: string) => `${ws(id)}/departments/${deptId}/restore`,
  },

  /// HR audit trail (HRMS FND-022 read API). Query string at the call site:
  /// scope, subjectType, subjectId, cursor, limit.
  audit: {
    bySubject: (id: string) => `${ws(id)}/audit-events`,
  },

  /// /workspaces/:wsId/approvals (+ /counts, decide actions) (FND-024/025/026,
  /// the HRMS unified approvals inbox). Query string (`scope`, `status`,
  /// `subjectType`, `cursor`, `limit`) stays at the call site, same
  /// convention as `audit` above.
  approvals: {
    list: (id: string) => `${ws(id)}/approvals`,
    counts: (id: string) => `${ws(id)}/approvals/counts`,
    approve: (id: string, approvalRequestId: string) =>
      `${ws(id)}/approvals/${approvalRequestId}/approve`,
    reject: (id: string, approvalRequestId: string) =>
      `${ws(id)}/approvals/${approvalRequestId}/reject`,
    cancel: (id: string, approvalRequestId: string) =>
      `${ws(id)}/approvals/${approvalRequestId}/cancel`,
    reResolve: (id: string, approvalRequestId: string) =>
      `${ws(id)}/approvals/${approvalRequestId}/re-resolve`,
  },

  /// /workspaces/:wsId/department-approvers (FND-025/026). Top level, NOT
  /// nested under /approvals, matching the shore's own routing decision
  /// (story section 12: api-surface.md over the frontend-ia.md draft).
  departmentApprovers: {
    list: (id: string) => `${ws(id)}/department-approvers`,
    detail: (id: string, departmentApproverId: string) =>
      `${ws(id)}/department-approvers/${departmentApproverId}`,
  },

  /// /workspaces/:wsId/employee-profiles (+ /designations, /employee-grades)
  /// (FND-011). The sensitive builders moved to the `payDetails` group above
  /// when ESS-10 built those routes; the note that once said the shore had no
  /// `/sensitive` route is no longer true. `designations` and
  /// `employeeGrades` are here even though FND-011 only edits
  /// `designationId`/`gradeId` on the profile, because the employment card
  /// (task 3) needs both catalogs for its pickers and river had no
  /// designation/grade api-path, schema, or hook at all before this task.
  hrCore: {
    employeeProfiles: (wsId: string) => `${ws(wsId)}/employee-profiles`,
    employeeDocuments: (wsId: string) => `${ws(wsId)}/employee-documents`,
    employeeProfile: (wsId: string, membershipId: string) =>
      `${ws(wsId)}/employee-profiles/${membershipId}`,
    workLocations: (wsId: string, membershipId: string) =>
      `${ws(wsId)}/employee-profiles/${membershipId}/work-locations`,
    employmentTerms: (wsId: string, membershipId: string) =>
      `${ws(wsId)}/employee-profiles/${membershipId}/employment-terms`,
    employmentTermsActivate: (wsId: string, membershipId: string, changeId: string) =>
      `${ws(wsId)}/employee-profiles/${membershipId}/employment-terms/${changeId}/activate`,
    designations: (wsId: string) => `${ws(wsId)}/designations`,
    openingRole: (wsId: string) => `${ws(wsId)}/designations/opening-role`,
    designation: (wsId: string, designationId: string) =>
      `${ws(wsId)}/designations/${designationId}`,
    employeeGrades: (wsId: string) => `${ws(wsId)}/employee-grades`,
    employeeGrade: (wsId: string, gradeId: string) => `${ws(wsId)}/employee-grades/${gradeId}`,
    hrSettings: (wsId: string) => `${ws(wsId)}/hr-settings`,

    /// /workspaces/:wsId/attachments (FND-020 shore module, FND-021 this
    /// kit's HTTP layer). Three builders for four routes, not four: POST
    /// (upload) and GET (list) both hang off the same bare `/attachments`
    /// URL, so they share `attachments` below and are distinguished by
    /// method at the call site, the same way `imports.csv` etc. do
    /// elsewhere in this file.
    attachments: (wsId: string) => `${ws(wsId)}/attachments`,
    attachment: (wsId: string, attachmentId: string) => `${ws(wsId)}/attachments/${attachmentId}`,
    attachmentDownload: (wsId: string, attachmentId: string) =>
      `${ws(wsId)}/attachments/${attachmentId}/download`,
  },

  /// /workspaces/:wsId/imports
  imports: {
    list: (id: string) => `${ws(id)}/imports`,
    csv: (id: string) => `${ws(id)}/imports/csv`,
    sheetFetch: (id: string) => `${ws(id)}/imports/sheet/fetch`,
    slackSync: (id: string) => `${ws(id)}/imports/slack/sync`,
    commit: (id: string, importId: string) => `${ws(id)}/imports/${importId}/commit`,
  },

  /// /workspaces/:wsId/integrations
  integrations: {
    slackStatus: (id: string) => `${ws(id)}/integrations/slack/status`,
    slackOauthStart: (id: string) => `${ws(id)}/integrations/slack/oauth-start`,
    slack: (id: string) => `${ws(id)}/integrations/slack`,
  },

  /// /workspaces/:wsId/home (member/manager/admin)
  home: {
    member: (id: string) => `${ws(id)}/home/member`,
    manager: (id: string) => `${ws(id)}/home/manager`,
    admin: (id: string) => `${ws(id)}/home/admin`,
  },

  /// /workspaces/:wsId/onboarding
  onboarding: {
    status: (id: string) => `${ws(id)}/onboarding/status`,
  },

  /// /workspaces/:wsId/comments
  comments: {
    /// Polymorphic nested comments under `<segment>/:parentId/comments`.
    list: (id: string, segment: string, parentId: string) =>
      `${ws(id)}/${segment}/${parentId}/comments`,
    detail: (id: string, commentId: string) => `${ws(id)}/comments/${commentId}`,
  },

  /// /workspaces/:wsId/reactions + /reactables
  reactions: {
    toggle: (id: string) => `${ws(id)}/reactions/toggle`,
    list: (id: string, reactableType: string, reactableId: string) =>
      `${ws(id)}/reactables/${reactableType}/${reactableId}/reactions`,
  },

  /// /workspaces/:wsId/writing-assistant
  writingAssistant: {
    drafts: (id: string) => `${ws(id)}/writing-assistant/drafts`,
  },

  /// /workspaces/:wsId/review-cycles (Module 14, Performance Reviews)
  reviews: {
    cycles: (id: string) => `${ws(id)}/review-cycles`,
    cycle: (id: string, cId: string) => `${ws(id)}/review-cycles/${cId}`,
    cycleTransition: (id: string, cId: string) => `${ws(id)}/review-cycles/${cId}/transition`,
    cycleDuplicate: (id: string, cId: string) => `${ws(id)}/review-cycles/${cId}/duplicate`,
    config: (id: string, cId: string) => `${ws(id)}/review-cycles/${cId}/config`,
    configLock: (id: string, cId: string) => `${ws(id)}/review-cycles/${cId}/config/lock`,
    participants: (id: string, cId: string) => `${ws(id)}/review-cycles/${cId}/participants`,
    participantsSummary: (id: string, cId: string) =>
      `${ws(id)}/review-cycles/${cId}/participants/summary`,
    hierarchyProgress: (id: string, cId: string) =>
      `${ws(id)}/review-cycles/${cId}/hierarchy-progress`,
    participantsMaterialize: (id: string, cId: string) =>
      `${ws(id)}/review-cycles/${cId}/participants/materialize`,
    participantsAdd: (id: string, cId: string) => `${ws(id)}/review-cycles/${cId}/participants/add`,
    participantRemove: (id: string, cId: string, pId: string) =>
      `${ws(id)}/review-cycles/${cId}/participants/${pId}/remove`,
    completeSignOff: (id: string, cId: string) =>
      `${ws(id)}/review-cycles/${cId}/complete-sign-off`,
    cycleRollout: (id: string, cId: string) => `${ws(id)}/review-cycles/${cId}/rollout`,
    myHistory: (id: string) => `${ws(id)}/review-history`,
    insightsDeptComparison: (id: string) => `${ws(id)}/review-insights/department-comparison`,
    insightsDistribution: (id: string) => `${ws(id)}/review-insights/rating-distribution`,
    insightsCalibration: (id: string, cId: string) =>
      `${ws(id)}/review-insights/manager-calibration?cycleId=${cId}`,
    hrCycleApprove: (id: string, cId: string) =>
      `${ws(id)}/review-cycles/${cId}/hr-approval/approve`,
    hrCycleRevoke: (id: string, cId: string) => `${ws(id)}/review-cycles/${cId}/hr-approval/revoke`,
    recurrences: (id: string) => `${ws(id)}/review-recurrences`,
    appraisalSchedules: (id: string) => `${ws(id)}/appraisal-schedules`,
    appraisalSchedule: (id: string, membershipId: string) =>
      `${ws(id)}/appraisal-schedules/${membershipId}`,
    appraisalCohortPolicy: (id: string) => `${ws(id)}/appraisal-cohort-policy`,
    appraisalCohortSpawns: (id: string) => `${ws(id)}/appraisal-cohort-spawns`,
    templates: (id: string) => `${ws(id)}/review-templates`,
    templateDetail: (id: string, tid: string) => `${ws(id)}/review-templates/${tid}`,
    templateDuplicate: (id: string, tid: string) => `${ws(id)}/review-templates/${tid}/duplicate`,
    templateQuestions: (id: string, tid: string) => `${ws(id)}/review-templates/${tid}/questions`,
    templateQuestionsReorder: (id: string, tid: string) =>
      `${ws(id)}/review-templates/${tid}/questions/reorder`,
    templateQuestion: (id: string, tid: string, qid: string) =>
      `${ws(id)}/review-templates/${tid}/questions/${qid}`,
    myTasks: (id: string) => `${ws(id)}/review-tasks/me`,
    myTeamTasks: (id: string) => `${ws(id)}/review-tasks/my-team`,
    myPeerTasks: (id: string) => `${ws(id)}/review-tasks/peer`,
    myRolloutTasks: (id: string) => `${ws(id)}/review-tasks/rollout`,
    participantCycles: (id: string) => `${ws(id)}/review-tasks/cycles`,
    participantCycleContext: (id: string, cId: string) =>
      `${ws(id)}/review-tasks/cycles/${cId}/context`,
    selfReview: (id: string, cId: string) => `${ws(id)}/review-cycles/${cId}/self-review`,
    selfReviewSubmit: (id: string, cId: string) =>
      `${ws(id)}/review-cycles/${cId}/self-review/submit`,
    managerReview: (id: string, cId: string, pId: string) =>
      `${ws(id)}/review-cycles/${cId}/participants/${pId}/manager-review`,
    managerResults: (id: string, cId: string, pId: string) =>
      `${ws(id)}/review-cycles/${cId}/participants/${pId}/results`,
    managerRating: (id: string, cId: string, pId: string) =>
      `${ws(id)}/review-cycles/${cId}/participants/${pId}/manager-rating`,
    managerReviewSubmit: (id: string, cId: string, pId: string) =>
      `${ws(id)}/review-cycles/${cId}/participants/${pId}/manager-review/submit`,
    managerRatingOverride: (id: string, cId: string, pId: string) =>
      `${ws(id)}/review-cycles/${cId}/participants/${pId}/manager-rating/override`,
    managerRatingsSubmitAll: (id: string, cId: string) =>
      `${ws(id)}/review-cycles/${cId}/manager-ratings/submit-all`,
    managerRatingsSubmitAllCycle: (id: string, cId: string) =>
      `${ws(id)}/review-cycles/${cId}/manager-ratings/submit-all-cycle`,
    signOff: (id: string, cId: string, pId: string) =>
      `${ws(id)}/review-cycles/${cId}/participants/${pId}/sign-off`,
    rollout: (id: string, cId: string, pId: string) =>
      `${ws(id)}/review-cycles/${cId}/participants/${pId}/rollout`,
    rolloutAction: (id: string, cId: string, pId: string, action: string) =>
      `${ws(id)}/review-cycles/${cId}/participants/${pId}/rollout/${action}`,
    peerResponse: (id: string, cId: string, pId: string) =>
      `${ws(id)}/review-cycles/${cId}/participants/${pId}/peer-review/me`,
    peerResponseSubmit: (id: string, cId: string, pId: string) =>
      `${ws(id)}/review-cycles/${cId}/participants/${pId}/peer-review/me/submit`,
    hrReview: (id: string, cId: string, pId: string) =>
      `${ws(id)}/review-cycles/${cId}/participants/${pId}/hr-review`,
    peerSelection: (id: string, cId: string, pId: string) =>
      `${ws(id)}/review-cycles/${cId}/participants/${pId}/peer/selection`,
    peerSelectionApprove: (id: string, cId: string, pId: string) =>
      `${ws(id)}/review-cycles/${cId}/participants/${pId}/peer/selection/approve`,
    peerClose: (id: string, cId: string, pId: string) =>
      `${ws(id)}/review-cycles/${cId}/participants/${pId}/peer/close`,
    // Inline peer selection: reviewee self-service (own participant only).
    peerSelfSelection: (id: string, cId: string, pId: string) =>
      `${ws(id)}/review-cycles/${cId}/participants/${pId}/peer/self-selection`,
    peerSelfSelectionRemove: (id: string, cId: string, pId: string, peerMembershipId: string) =>
      `${ws(id)}/review-cycles/${cId}/participants/${pId}/peer/self-selection/${peerMembershipId}`,
    peerSelfSelectionConfirm: (id: string, cId: string, pId: string) =>
      `${ws(id)}/review-cycles/${cId}/participants/${pId}/peer/self-selection/confirm`,
    // Inline peer selection: manager/admin side (named responses + requests).
    peerResponses: (id: string, cId: string, pId: string) =>
      `${ws(id)}/review-cycles/${cId}/participants/${pId}/peer/responses`,
    peerRequests: (id: string, cId: string, pId: string) =>
      `${ws(id)}/review-cycles/${cId}/participants/${pId}/peer/requests`,
    peerRequestRemove: (id: string, cId: string, pId: string, peerMembershipId: string) =>
      `${ws(id)}/review-cycles/${cId}/participants/${pId}/peer/requests/${peerMembershipId}`,
    suggestions: (id: string, cId: string, pId: string, persona: string) =>
      `${ws(id)}/review-cycles/${cId}/participants/${pId}/suggestions/${persona}`,
    suggestionsGenerate: (id: string, cId: string, pId: string, persona: string) =>
      `${ws(id)}/review-cycles/${cId}/participants/${pId}/suggestions/${persona}/generate`,
  },
  /// HRMS-EXP-001. Final path segments come from api-surface.md 2.4, which
  /// owns them over the frontend-ia draft spellings. No vite proxy entry is
  /// needed: every route lives under `/workspaces`, already forwarded.
  expenses: {
    categories: (id: string) => `${ws(id)}/expense-categories`,
    category: (id: string, categoryId: string) => `${ws(id)}/expense-categories/${categoryId}`,
    /// HRMS-EXP-004. Claim CRUD and the three state transitions, from
    /// api-surface.md 2.4 which owns the final segments. All under
    /// `/workspaces`, so the dev proxy needs no entry.
    claims: (id: string) => `${ws(id)}/expense-claims`,
    claimPayrollCurrency: (id: string) => `${ws(id)}/expense-claims/payroll-currency`,
    claim: (id: string, claimId: string) => `${ws(id)}/expense-claims/${claimId}`,
    claimSubmit: (id: string, claimId: string) => `${ws(id)}/expense-claims/${claimId}/submit`,
    claimRecall: (id: string, claimId: string) => `${ws(id)}/expense-claims/${claimId}/recall`,
    claimCancel: (id: string, claimId: string) => `${ws(id)}/expense-claims/${claimId}/cancel`,
    /// HRMS-EXP-003. Per-line sanction during approval. Its own controller on
    /// the shore, the same path prefix here.
    claimSanction: (id: string, claimId: string) => `${ws(id)}/expense-claims/${claimId}/sanction`,
    /// HRMS-EXP-003 settlement. Two calls rather than one editable figure:
    /// `amount` is the money that moved on THIS payment, never a running
    /// total, so a retry cannot silently pay twice.
    claimMarkPaid: (id: string, claimId: string) => `${ws(id)}/expense-claims/${claimId}/mark-paid`,
    claimReversePayment: (id: string, claimId: string) =>
      `${ws(id)}/expense-claims/${claimId}/reverse-payment`,
    /// HRMS-EXP-010. Money requested before it is spent. No detail path: the
    /// row is the whole record (design 5.3), so a deep link lands on the list.
    advances: (id: string) => `${ws(id)}/employee-advances`,
    advanceCancel: (id: string, advanceId: string) =>
      `${ws(id)}/employee-advances/${advanceId}/cancel`,
    advanceMarkPaid: (id: string, advanceId: string) =>
      `${ws(id)}/employee-advances/${advanceId}/mark-paid`,
    advanceRecordReturn: (id: string, advanceId: string) =>
      `${ws(id)}/employee-advances/${advanceId}/record-return`,
    /// HRMS-EXP-020. Travel authorization: no money moves, so it lives beside
    /// claims rather than inside them.
    travelRequests: (id: string) => `${ws(id)}/travel-requests`,
    travelRequest: (id: string, travelRequestId: string) =>
      `${ws(id)}/travel-requests/${travelRequestId}`,
    travelRequestCancel: (id: string, travelRequestId: string) =>
      `${ws(id)}/travel-requests/${travelRequestId}/cancel`,
    /// HRMS-EXP-030. The report and its CSV, from api-surface.md 2.4. The two
    /// are one contract with one filter set (from, to, categoryId,
    /// membershipId, groupBy), so they land together: `report` is the JSON
    /// analytics reads, `reportExport` is the download EXP-005 puts behind
    /// EXPENSE_REPORT_EXPORT. The export is a browser navigation to a
    /// text/csv attachment, not an `apiCall`, so it never meets the envelope.
    report: (id: string) => `${ws(id)}/expense-report`,
    reportExport: (id: string) => `${ws(id)}/expense-report/export.csv`,
  },
};
